import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Autocomplete, OutlinedInput } from "~/components/UI";
import { useField, FormikHandlers } from "formik";
import _ from "lodash";
import api from "~/api";

interface FromikSelectMDESMerchantProps {
  name: string;
  defaultInputValue?: string;
  filterOptions?: (
    options: MDESMerchantsApi.mdesMerchantDto[],
    state: any
  ) => MDESMerchantsApi.mdesMerchantDto[];
}

interface SelectMDESMerchantProps {
  inputValue?: string;
  name?: string;
  onChange?: (merchant?: MDESMerchantsApi.mdesMerchantDto) => void;
  onChangeValue?: (value?: string) => void;
  onBlur?: FormikHandlers["handleBlur"];
  error?: string | undefined;
  touched?: boolean;
  autoClear?: boolean;
  filterOptions?: (
    options: MDESMerchantsApi.mdesMerchantDto[],
    state: any
  ) => MDESMerchantsApi.mdesMerchantDto[];
}

const requestSearchMerchant = (search: string) =>
  api.MDESMerchants.getAll(20, 1, { search });

export const SelectMDESMerchant = React.memo(
  ({
    name = "",
    onChange,
    onChangeValue,
    onBlur = () => {},
    touched,
    error,
    filterOptions,
    inputValue
  }: SelectMDESMerchantProps) => {
    const [value, setValue] = useState<string | undefined>(inputValue || "");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<
      MDESMerchantsApi.mdesMerchantDto[]
    >([]);
    const [merchant, setMerchant] =
      useState<Partial<MDESMerchantsApi.mdesMerchantDto> | null>(null);

    const searchMerchantes = useMemo(
      () =>
        _.debounce((merchantId: string) => {
          setLoading(true);
          requestSearchMerchant(merchantId)
            .then(({ data }) => {
              setSearchResults(data.data);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 500),
      []
    );

    const onChangeInputValue = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setValue(e.target.value);
      onChangeValue && onChangeValue(e.target.value);
      searchMerchantes(e.target.value || "");
    };

    const onChangeAutocomplete = useCallback(
      (_e: React.ChangeEvent<{}>, newValue: any) => {
        if (newValue) {
          setValue(newValue.mdesMerchantId);
          setMerchant(newValue);
        } else {
          setValue("");
          setMerchant(null);
        }
        onChange && onChange(newValue);
      },
      [onChange]
    );

    const getOptionSelected = useCallback(
      (
        o: MDESMerchantsApi.mdesMerchantDto,
        value: MDESMerchantsApi.mdesMerchantDto
      ) => o.mdesMerchantId === value.mdesMerchantId,
      []
    );

    const getOptionLabel = useCallback(
      (o: MDESMerchantsApi.mdesMerchantDto) => o.mdesMerchantId || "",
      []
    );

    useEffect(() => {
      if (name && !inputValue) {
        setValue("");
        setMerchant(null);
        searchMerchantes("");
      }
    }, [name, inputValue]);

    useEffect(() => {
      searchMerchantes("");
    }, [searchMerchantes]);

    return (
      <Autocomplete
        value={merchant}
        onChange={onChangeAutocomplete}
        getOptionSelected={getOptionSelected}
        onBlur={onBlur}
        noOptionsText="No MDES Merchantes"
        options={searchResults}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        filterSelectedOptions={true}
        loading={loading}
        disableClearable
        renderInput={(params: any) => (
          <OutlinedInput
            className={params.InputProps.className}
            endAdornment={params.InputProps.endAdornment}
            inputProps={_.omit(params.inputProps, "value")}
            value={params.inputProps.value || value}
            touched={touched}
            error={error}
            fullWidth
            onChange={onChangeInputValue}
          />
        )}
      />
    );
  }
);

const FromikContainer = ({
  name,
  defaultInputValue,
  ...other
}: FromikSelectMDESMerchantProps & SelectMDESMerchantProps) => {
  const [field, meta] = useField(name);
  const { error, touched } = meta;

  const onChange = useCallback(
    (merchant?: MDESMerchantsApi.mdesMerchantDto) => {
      field.onChange({
        target: {
          name,
          value: merchant ? merchant.mdesMerchantId : ""
        }
      });
    },
    [field, name]
  );

  return (
    <SelectMDESMerchant
      {...field}
      {...other}
      onChange={onChange}
      touched={touched}
      error={error}
      inputValue={defaultInputValue || field.value}
    />
  );
};

export default React.memo(FromikContainer);
